<template>
    <div class="cl-builder">
        <!-- Domain rail -->
        <aside class="cl-rail">
            <div class="cl-rail-head">
                <span class="cl-rail-title">Domains</span>
                <span class="cl-rail-count">{{ domains.length }}</span>
            </div>

            <div class="cl-rail-list">
                <button v-for="(domain, i) in domains" :key="`domain-${i}`" type="button" class="cl-rail-item"
                    :class="{ active: i === activeDomain, invalid: domainHasError(i) }" @click="activeDomain = i">
                    <span class="cl-rail-name">{{ domain.name || 'Untitled domain' }}</span>
                    <span class="cl-rail-meta">
                        {{ countOf(domain.datasets) }} datasets · {{ countOf(domain.metrics) }} metrics
                    </span>
                    <i v-if="domainHasError(i)" class="fa fa-circle-exclamation cl-rail-flag"
                        title="This domain has validation errors" data-bs-toggle="tooltip"
                        data-bs-placement="right"></i>
                </button>

                <p v-if="!domains.length" class="cl-rail-empty">No domains yet.</p>
            </div>

            <button v-if="editable" type="button" class="btn btn-sm btn-light w-100 cl-rail-add" @click="addDomain">
                <i class="fa fa-plus me-1"></i> Add domain
            </button>
        </aside>

        <!-- Detail panel -->
        <section class="cl-detail">
            <template v-if="domain">
                <!-- Domain header -->
                <header class="cl-detail-head">
                    <div class="cl-detail-titles">
                        <input v-model="domain.name" class="cl-title-input" placeholder="domain_name"
                            :disabled="!editable" @input="emitChange" />
                        <input v-model="domain.description" class="cl-subtitle-input"
                            placeholder="What this domain covers" :disabled="!editable" @input="emitChange" />
                    </div>
                    <button v-if="editable" type="button" class="btn btn-sm btn-outline-danger"
                        @click="removeDomain(activeDomain)" title="Delete domain">
                        <i class="fa fa-trash"></i>
                    </button>
                </header>

                <div class="cl-field-row">
                    <label class="cl-label">Synonyms</label>
                    <TagInput v-model="domain.synonyms" :disabled="!editable" placeholder="Other names for this domain"
                        @update:modelValue="emitChange" />
                </div>

                <!-- Datasets -->
                <div class="cl-section">
                    <div class="cl-section-head">
                        <h5 class="cl-section-title">
                            <i class="fa fa-table me-2"></i>Datasets
                            <span class="cl-badge">{{ countOf(domain.datasets) }}</span>
                        </h5>
                        <button v-if="editable" type="button" class="btn btn-sm btn-light" @click="addDataset">
                            <i class="fa fa-plus me-1"></i> Add dataset
                        </button>
                    </div>

                    <p v-if="!countOf(domain.datasets)" class="cl-empty">
                        No datasets described yet. Add one so PAICE knows what tables it can reason about.
                    </p>

                    <article v-for="(dataset, di) in domain.datasets" :key="`ds-${activeDomain}-${di}`" class="cl-card"
                        :class="{ open: isOpen('dataset', di) }">
                        <button type="button" class="cl-card-head" @click="toggle('dataset', di)">
                            <i class="fa cl-caret"
                                :class="isOpen('dataset', di) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                            <span class="cl-card-name">{{ dataset.name || 'Untitled dataset' }}</span>
                            <span class="cl-card-meta">
                                {{ countOf(dataset.fields) }} fields · {{ countOf(dataset.joins) }} joins
                            </span>
                            <span v-for="tag in dataset.tags" :key="tag" class="cl-inline-tag">{{ tag }}</span>
                        </button>

                        <div v-show="isOpen('dataset', di)" class="cl-card-body">
                            <div class="row g-3">
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Name</label>
                                    <input v-model="dataset.name" class="form-control form-control-sm"
                                        placeholder="table_or_dataset_name" :disabled="!editable" @input="emitChange" />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Description</label>
                                    <input v-model="dataset.description" class="form-control form-control-sm"
                                        placeholder="One row per…" :disabled="!editable" @input="emitChange" />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Synonyms</label>
                                    <TagInput v-model="dataset.synonyms" :disabled="!editable"
                                        @update:modelValue="emitChange" />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Tags</label>
                                    <TagInput v-model="dataset.tags" variant="tag" :disabled="!editable"
                                        @update:modelValue="emitChange" />
                                </div>
                            </div>

                            <!-- Fields -->
                            <div class="cl-subsection">
                                <div class="cl-subsection-head">
                                    <span class="cl-subsection-title">
                                        Fields <span class="cl-badge">{{ countOf(dataset.fields) }}</span>
                                    </span>
                                    <button v-if="editable" type="button" class="btn btn-sm btn-link cl-add-link"
                                        @click="addField(dataset)">
                                        <i class="fa fa-plus me-1"></i>Add field
                                    </button>
                                </div>

                                <div v-for="(field, fi) in dataset.fields" :key="`f-${di}-${fi}`" class="cl-nested">
                                    <div class="cl-nested-head">
                                        <div class="cl-cell">
                                            <label class="cl-label">Field name</label>
                                            <input v-model="field.name" class="form-control form-control-sm cl-mono"
                                                placeholder="column_name" :disabled="!editable" @input="emitChange" />
                                        </div>
                                        <div class="cl-cell">
                                            <label class="cl-label">Tags</label>
                                            <TagInput v-model="field.tags" variant="tag" :disabled="!editable"
                                                placeholder="dimension, identifier…" @update:modelValue="emitChange" />
                                        </div>
                                        <button v-if="editable" type="button" class="btn btn-sm cl-icon-btn"
                                            @click="dataset.fields.splice(fi, 1); emitChange()" title="Remove field">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>

                                    <div class="cl-nested-body">
                                        <div class="cl-field-row">
                                            <label class="cl-label">Synonyms</label>
                                            <TagInput v-model="field.synonyms" :disabled="!editable"
                                                @update:modelValue="emitChange" />
                                        </div>

                                        <!-- Value groups -->
                                        <div class="cl-vg">
                                            <div class="cl-vg-head">
                                                <span class="cl-label mb-0">
                                                    Value groups
                                                    <span class="cl-badge">{{ countOf(field.value_groups) }}</span>
                                                    <i class="fa fa-circle-info cl-hint"
                                                        title="Map a business term like 'mobile' to the SQL that selects it"
                                                        data-bs-toggle="tooltip" data-bs-placement="right"></i>
                                                </span>
                                                <button v-if="editable" type="button"
                                                    class="btn btn-sm btn-link cl-add-link"
                                                    @click="addValueGroup(field)">
                                                    <i class="fa fa-plus me-1"></i>Add
                                                </button>
                                            </div>

                                            <div v-for="(vg, vi) in field.value_groups" :key="`vg-${fi}-${vi}`"
                                                class="cl-vg-row">
                                                <div class="cl-vg-main">
                                                    <div class="cl-cell">
                                                        <label class="cl-label">Business term</label>
                                                        <input v-model="vg.business_term"
                                                            class="form-control form-control-sm" placeholder="mobile"
                                                            :disabled="!editable" @input="emitChange" />
                                                    </div>
                                                    <div class="cl-cell">
                                                        <label class="cl-label">SQL</label>
                                                        <input v-model="vg.sql"
                                                            class="form-control form-control-sm cl-mono"
                                                            placeholder="device_type IN ('phone')" :disabled="!editable"
                                                            @input="emitChange" />
                                                    </div>
                                                    <button v-if="editable" type="button" class="btn btn-sm cl-icon-btn"
                                                        @click="field.value_groups.splice(vi, 1); emitChange()"
                                                        title="Remove value group">
                                                        <i class="fa fa-trash"></i>
                                                    </button>
                                                </div>

                                                <div class="cl-cell cl-vg-synonyms">
                                                    <label class="cl-label">Synonyms</label>
                                                    <TagInput v-model="vg.synonyms" :disabled="!editable"
                                                        placeholder="phone, handset…" @update:modelValue="emitChange" />
                                                </div>
                                            </div>

                                            <p v-if="!countOf(field.value_groups)" class="cl-vg-empty">
                                                No value groups — PAICE will match this field's raw values only.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <p v-if="!countOf(dataset.fields)" class="cl-empty sm">No fields described.</p>
                            </div>

                            <!-- Joins -->
                            <div class="cl-subsection">
                                <div class="cl-subsection-head">
                                    <span class="cl-subsection-title">
                                        Joins <span class="cl-badge">{{ countOf(dataset.joins) }}</span>
                                    </span>
                                    <button v-if="editable" type="button" class="btn btn-sm btn-link cl-add-link"
                                        @click="addJoin(dataset)">
                                        <i class="fa fa-plus me-1"></i>Add join
                                    </button>
                                </div>

                                <div v-for="(join, ji) in dataset.joins" :key="`j-${di}-${ji}`" class="cl-join-row">
                                    <div class="cl-cell">
                                        <label class="cl-label">Target entity</label>
                                        <input v-model="join.target_entity" class="form-control form-control-sm cl-mono"
                                            placeholder="sessions" :disabled="!editable" @input="emitChange" />
                                    </div>
                                    <div class="cl-cell">
                                        <label class="cl-label">Relation</label>
                                        <select v-model="join.relation" class="form-select form-select-sm"
                                            :disabled="!editable" @change="emitChange">
                                            <option v-for="rel in relations" :key="rel" :value="rel">
                                                {{ rel.replace(/_/g, ' ') }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="cl-cell">
                                        <label class="cl-label">Join condition</label>
                                        <input v-model="join.sql" class="form-control form-control-sm cl-mono"
                                            placeholder="a.id = b.id" :disabled="!editable" @input="emitChange" />
                                    </div>
                                    <button v-if="editable" type="button" class="btn btn-sm cl-icon-btn"
                                        @click="dataset.joins.splice(ji, 1); emitChange()" title="Remove join">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </div>

                                <p v-if="!countOf(dataset.joins)" class="cl-empty sm">No joins described.</p>
                            </div>

                            <div v-if="editable" class="cl-card-foot">
                                <button type="button" class="btn btn-sm btn-link text-danger"
                                    @click="removeDataset(di)">
                                    <i class="fa fa-trash me-1 pe-1"></i>Delete dataset
                                </button>
                            </div>
                        </div>
                    </article>
                </div>

                <!-- Metrics -->
                <div class="cl-section">
                    <div class="cl-section-head">
                        <h5 class="cl-section-title">
                            <i class="fa fa-chart-simple me-2"></i>Metrics
                            <span class="cl-badge">{{ countOf(domain.metrics) }}</span>
                        </h5>
                        <button v-if="editable" type="button" class="btn btn-sm btn-light" @click="addMetric">
                            <i class="fa fa-plus me-1"></i> Add metric
                        </button>
                    </div>

                    <p v-if="!countOf(domain.metrics)" class="cl-empty">
                        No metrics defined. Metrics teach PAICE how your business measures things.
                    </p>

                    <article v-for="(metric, mi) in domain.metrics" :key="`m-${activeDomain}-${mi}`" class="cl-card"
                        :class="{ open: isOpen('metric', mi) }">
                        <button type="button" class="cl-card-head" @click="toggle('metric', mi)">
                            <i class="fa cl-caret"
                                :class="isOpen('metric', mi) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                            <span class="cl-card-name">{{ metric.name || 'Untitled metric' }}</span>
                            <code v-if="metric.sql" class="cl-card-sql">{{ metric.sql }}</code>
                        </button>

                        <div v-show="isOpen('metric', mi)" class="cl-card-body">
                            <div class="row g-3">
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Name</label>
                                    <input v-model="metric.name" class="form-control form-control-sm"
                                        placeholder="total_pageviews" :disabled="!editable" @input="emitChange" />
                                </div>
                                <div class="col-12 col-md-6">
                                    <label class="cl-label">Description</label>
                                    <input v-model="metric.description" class="form-control form-control-sm"
                                        placeholder="Count of page views" :disabled="!editable" @input="emitChange" />
                                </div>
                                <div class="col-12">
                                    <label class="cl-label">Synonyms</label>
                                    <TagInput v-model="metric.synonyms" :disabled="!editable"
                                        @update:modelValue="emitChange" />
                                </div>
                                <div class="col-12">
                                    <label class="cl-label">SQL</label>
                                    <input v-model="metric.sql" class="form-control form-control-sm cl-mono"
                                        placeholder="COUNT(*)" :disabled="!editable" @input="emitChange" />
                                </div>
                            </div>

                            <div v-if="editable" class="cl-card-foot">
                                <button type="button" class="btn btn-sm btn-link text-danger" @click="removeMetric(mi)">
                                    <i class="fa fa-trash me-1 pe-1"></i>Delete metric
                                </button>
                            </div>
                        </div>
                    </article>
                </div>
            </template>

            <div v-else class="cl-blank">
                <i class="fa fa-layer-group"></i>
                <p class="mb-2">This context layer has no domains.</p>
                <button v-if="editable" type="button" class="btn btn-sm btn-primary" @click="addDomain">
                    Add the first domain
                </button>
            </div>
        </section>
    </div>
</template>

<script>
/**
 * Form-based editor for the context layer, as an alternative to hand-writing
 * the YAML.
 *
 * Master–detail rather than one long nested stack: the schema goes four levels
 * deep (domains → datasets → fields → value_groups) and a real client's layer
 * has dozens of datasets, so rendering every domain at once would produce a
 * page nobody can navigate. One domain is in view at a time; within it,
 * datasets and metrics are collapsed cards.
 *
 * The component owns a working copy and emits the whole document on every
 * edit. It never parses or serializes YAML itself — the parent owns that, so
 * there is exactly one place where the text and the object can diverge.
 */
import TagInput from './TagInput.vue';
import {
    JOIN_RELATIONS, blankDomain, blankDataset, blankField, blankJoin, blankMetric, blankValueGroup,
} from '@/utils/contextLayer';

export default {
    name: 'ContextLayerBuilder',
    components: { TagInput },
    props: {
        /** The parsed context layer document. */
        modelValue: { type: Object, required: true },
        editable: { type: Boolean, default: true },
        /** Validation errors from the parent, pathed `domains.0.datasets.1.name`. */
        errors: { type: Array, default: () => [] },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            doc: null,
            activeDomain: 0,
            relations: JOIN_RELATIONS,
            // Which collapsed cards are expanded, keyed `dataset:0` / `metric:2`.
            // Reset whenever the active domain changes, since the indices mean
            // something different there.
            open: {},
        };
    },
    computed: {
        domains() {
            return this.doc?.domains || [];
        },
        domain() {
            return this.domains[this.activeDomain] || null;
        },
        /** Domain indices carrying at least one error, for the rail's warning flags. */
        domainsWithErrors() {
            const flagged = new Set();
            this.errors.forEach((e) => {
                const match = /^domains\.(\d+)/.exec(e.path || '');
                if (match) flagged.add(Number(match[1]));
            });
            return flagged;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                // Only adopt the incoming document when it differs from what we
                // already hold; otherwise our own emit would echo back and reset
                // the cursor mid-typing.
                if (JSON.stringify(val) === JSON.stringify(this.doc)) return;
                this.doc = this.normalize(val);
                if (this.activeDomain >= this.domains.length) this.activeDomain = 0;
            },
        },
        activeDomain() {
            this.open = {};
        },
    },
    methods: {
        countOf(list) {
            return Array.isArray(list) ? list.length : 0;
        },

        /**
         * Fill in the optional collections the schema defaults to `[]`.
         *
         * A hand-authored document legitimately omits them, but the form binds
         * v-model straight to these arrays, so they have to exist before render.
         */
        normalize(val) {
            const doc = JSON.parse(JSON.stringify(val || {}));
            doc.version = doc.version || '1.0';
            doc.domains = Array.isArray(doc.domains) ? doc.domains : [];
            doc.domains.forEach((domain) => {
                domain.synonyms = domain.synonyms || [];
                domain.datasets = domain.datasets || [];
                domain.metrics = domain.metrics || [];
                domain.datasets.forEach((dataset) => {
                    dataset.synonyms = dataset.synonyms || [];
                    dataset.tags = dataset.tags || [];
                    dataset.fields = dataset.fields || [];
                    dataset.joins = dataset.joins || [];
                    dataset.fields.forEach((field) => {
                        field.synonyms = field.synonyms || [];
                        field.tags = field.tags || [];
                        field.value_groups = field.value_groups || [];
                        field.value_groups.forEach((vg) => {
                            vg.synonyms = vg.synonyms || [];
                        });
                    });
                });
                domain.metrics.forEach((metric) => {
                    metric.synonyms = metric.synonyms || [];
                });
            });
            return doc;
        },

        domainHasError(index) {
            return this.domainsWithErrors.has(index);
        },

        isOpen(kind, index) {
            return !!this.open[`${kind}:${index}`];
        },
        toggle(kind, index) {
            const key = `${kind}:${index}`;
            this.open = { ...this.open, [key]: !this.open[key] };
        },

        emitChange() {
            this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.doc)));
        },

        addDomain() {
            this.doc.domains.push(blankDomain());
            this.activeDomain = this.doc.domains.length - 1;
            this.emitChange();
        },
        removeDomain(index) {
            this.doc.domains.splice(index, 1);
            if (this.activeDomain >= this.doc.domains.length) {
                this.activeDomain = Math.max(0, this.doc.domains.length - 1);
            }
            this.emitChange();
        },
        addDataset() {
            this.domain.datasets.push(blankDataset());
            // Open the new card — it is empty, so leaving it collapsed hides the
            // only thing the user came to do.
            this.open = { ...this.open, [`dataset:${this.domain.datasets.length - 1}`]: true };
            this.emitChange();
        },
        removeDataset(index) {
            this.domain.datasets.splice(index, 1);
            this.open = {};
            this.emitChange();
        },
        addMetric() {
            this.domain.metrics.push(blankMetric());
            this.open = { ...this.open, [`metric:${this.domain.metrics.length - 1}`]: true };
            this.emitChange();
        },
        removeMetric(index) {
            this.domain.metrics.splice(index, 1);
            this.open = {};
            this.emitChange();
        },
        addField(dataset) {
            dataset.fields.push(blankField());
            this.emitChange();
        },
        addJoin(dataset) {
            dataset.joins.push(blankJoin());
            this.emitChange();
        },
        addValueGroup(field) {
            field.value_groups.push(blankValueGroup());
            this.emitChange();
        },
    },
};
</script>

<style scoped>
.cl-builder {
    display: grid;
    grid-template-columns: 15rem minmax(0, 1fr);
    gap: 1.25rem;
    align-items: start;
}

/* Domain rail */
.cl-rail {
    border: 1px solid #edf2f9;
    border-radius: 0.5rem;
    background: #f9fbfd;
    padding: 0.6rem;
    position: sticky;
    top: 1rem;
}

.cl-rail-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.25rem 0.5rem;
}

.cl-rail-title {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #95aac9;
}

.cl-rail-count {
    font-size: 0.65rem;
    font-weight: 600;
    color: #6e84a3;
    background: #edf2f9;
    border-radius: 999px;
    padding: 0.05rem 0.4rem;
}

.cl-rail-list {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    max-height: 22rem;
    overflow-y: auto;
    margin-bottom: 0.5rem;
}

.cl-rail-item {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    width: 100%;
    text-align: left;
    border: 0;
    background: transparent;
    border-left: 2.5px solid transparent;
    border-radius: 0.2rem;
    padding: 0.4rem 0.5rem;
    transition: background-color 0.12s ease, border-color 0.12s ease;
}

.cl-rail-item:hover {
    background: #fff;
}

.cl-rail-item.active {
    background: #e8f1fd;
    border-left-color: #2c7be5;
}

.cl-rail-name {
    font-size: 0.8rem;
    font-weight: 500;
    color: #12263f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cl-rail-item.active .cl-rail-name {
    color: #2c7be5;
}

.cl-rail-meta {
    font-size: 0.68rem;
    color: #95aac9;
}

.cl-rail-flag {
    position: absolute;
    top: 0.45rem;
    right: 0.45rem;
    font-size: 0.7rem;
    color: #e63757;
}

.cl-rail-empty {
    font-size: 0.75rem;
    color: #b1c2d9;
    padding: 0.5rem 0.25rem;
    margin: 0;
}

.cl-rail-add {
    font-size: 0.75rem;
}

/* Detail panel */
.cl-detail {
    min-width: 0;
}

.cl-detail-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #edf2f9;
    margin-bottom: 1rem;
}

.cl-detail-titles {
    flex: 1;
    min-width: 0;
}

/* Borderless inputs so the domain name reads as a heading, not a form field,
   until it is focused. */
.cl-title-input,
.cl-subtitle-input {
    display: block;
    width: 100%;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 0.25rem;
    padding: 0.15rem 0.35rem;
    margin-left: -0.35rem;
}

.cl-title-input {
    font-size: 1.05rem;
    font-weight: 600;
    color: #12263f;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.cl-subtitle-input {
    font-size: 0.78rem;
    color: #6e84a3;
}

.cl-title-input:hover,
.cl-subtitle-input:hover {
    border-color: #e3ebf6;
}

.cl-title-input:focus,
.cl-subtitle-input:focus {
    outline: 0;
    background: #fff;
    border-color: #2c7be5;
}

.cl-title-input::placeholder,
.cl-subtitle-input::placeholder {
    color: #b1c2d9;
    font-weight: 400;
}

.cl-label {
    display: block;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #95aac9;
    margin-bottom: 0.25rem;
}

.cl-vg-head .cl-label {
    display: inline-flex;
    align-items: center;
}

.cl-hint {
    color: #b1c2d9;
    text-transform: none;
    margin-left: 0.2rem;
}

.cl-field-row {
    margin-bottom: 1rem;
}

/* Sections */
.cl-section {
    margin-top: 1.5rem;
}

.cl-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.6rem;
}

.cl-section-title {
    display: inline-flex;
    align-items: center;
    font-size: 0.85rem;
    font-weight: 600;
    color: #12263f;
    margin: 0;
}

.cl-section-title i {
    color: #95aac9;
    font-size: 0.75rem;
}

.cl-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
    color: #6e84a3;
    background: #edf2f9;
    border-radius: 999px;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.35rem;
    margin-left: 0.35rem;
}

.cl-empty {
    font-size: 0.75rem;
    color: #b1c2d9;
    background: #f4f7fb;
    border: 1px dashed #e3ebf6;
    border-radius: 0.375rem;
    padding: 0.9rem;
    text-align: center;
    margin: 0;
}

.cl-empty.sm {
    padding: 0.5rem;
    font-size: 0.72rem;
    text-align: left;
}

/* Collapsible cards */
.cl-card {
    border: 1px solid #e3ebf6;
    border-radius: 0.375rem;
    margin-bottom: 0.4rem;
    background: #f9fbfd;
    overflow: hidden;
}

.cl-card.open {
    border-color: #d2ddec;
    box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06);
}

.cl-card-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 0.55rem 0.7rem;
}

.cl-card-head:hover {
    background: #f9fbfd;
}

.cl-caret {
    font-size: 0.65rem;
    color: #95aac9;
    width: 0.75rem;
    flex-shrink: 0;
}

.cl-card-name {
    font-size: 0.82rem;
    font-weight: 500;
    color: #12263f;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.cl-card-meta {
    font-size: 0.7rem;
    color: #95aac9;
    margin-left: auto;
    white-space: nowrap;
}

.cl-card-sql {
    font-size: 0.7rem;
    color: #6e84a3;
    background: #f9fbfd;
    border-radius: 0.2rem;
    padding: 0.05rem 0.3rem;
    margin-left: auto;
    max-width: 16rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cl-inline-tag {
    font-size: 0.62rem;
    font-weight: 500;
    color: #6e84a3;
    background: #edf2f9;
    border-radius: 0.2rem;
    padding: 0.05rem 0.3rem;
}

.cl-card-body {
    padding: 0.85rem 0.7rem 0.7rem;
    border-top: 1px solid #edf2f9;
    background: #fcfdfe;
}

.cl-card-foot {
    margin-top: 0.6rem;
    padding-top: 0.5rem;
    /* border-top: 1px solid #edf2f9; */
    text-align: right;
}

.cl-card-foot .btn {
    font-size: 0.73rem;
}

/* Sub-sections inside a dataset card */
.cl-subsection {
    margin-top: 1rem;
}

.cl-subsection-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #edf2f9;
    padding-bottom: 0.25rem;
    margin-bottom: 0.5rem;
}

.cl-subsection-title {
    display: inline-flex;
    align-items: center;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #6e84a3;
}

.cl-add-link {
    font-size: 0.72rem;
    padding: 0;
}

/* A field, with its value groups nested one level deeper. Each depth gets its
   own fill and a left accent border, as in the audience criterion builder, so
   "which level am I editing" is answerable at a glance. */
.cl-nested {
    padding: 0.7rem 0.75rem;
    margin-bottom: 0.6rem;
    border: 1px solid #e3ebf6;
    border-left: 3px solid #d2ddec;
    border-radius: 0.3rem;
    background: #f4f7fb;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.cl-nested:hover {
    border-left-color: #2c7be5;
    box-shadow: 0 1px 3px rgba(18, 38, 63, 0.06);
}

.cl-nested-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) auto;
    gap: 0.5rem;
    /* `start`, not `end` — the tag inputs grow with their chips, and an end
       alignment would drag the neighbouring labels down with them. */
    align-items: start;
}

.cl-nested-body {
    padding: 0.65rem 0 0;
    margin-top: 0.65rem;
    border-top: 1px solid #e3ebf6;
}

/* Value groups: one level deeper again, so a lighter card on the field's fill. */
.cl-vg {
    margin-top: 0.6rem;
    padding: 0.6rem;
    border-radius: 0.3rem;
    border: 1px dashed #dbe4ef;
    background: #ffffff;
}

.cl-vg-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.4rem;
}

.cl-vg-empty {
    font-size: 0.7rem;
    color: #b1c2d9;
    margin: 0;
}

/* Value group: term + SQL on the first row, synonyms on its own row below.
   Synonyms is a chip input that wraps to any height, so keeping it out of the
   first row is what stops it dictating that row's layout. */
.cl-vg-row {
    padding: 0.5rem;
    margin-bottom: 0.4rem;
    border-radius: 0.25rem;
    border-left: 2px solid #e3ebf6;
    background: #fff;
}

.cl-vg-main {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.6fr) auto;
    gap: 0.5rem;
    align-items: start;
}

.cl-vg-synonyms {
    margin-top: 0.4rem;
}

.cl-vg-row:last-child {
    margin-bottom: 0;
}

.cl-vg-row:hover {
    border-left-color: #2c7be5;
}

.cl-join-row {
    display: grid;
    gap: 0.5rem;
    align-items: start;
    grid-template-columns: minmax(0, 1fr) 9rem minmax(0, 1.6fr) auto;
    padding: 0.55rem 0.6rem;
    margin-bottom: 0.4rem;
    border-radius: 0.375rem;
    border: 1px solid #e3ebf6;
    border-left: 3px solid #d2ddec;
    background: #f4f7fb;
    transition: border-color 0.12s ease;
}

.cl-join-row:hover {
    border-left-color: #2c7be5;
}

/* Labelled cell inside a grid row — the label must not stretch the row. */
.cl-cell {
    min-width: 0;
}

.cl-cell .cl-label {
    margin-bottom: 0.15rem;
}

.cl-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
}

.cl-icon-btn {
    color: #b1c2d9;
    padding: 0.15rem 0.35rem;
    border: 0;
    background: transparent;
    font-size: 0.72rem;
}

/* Rows align on `start`, so the button — which has no label above it — needs
   the label's height as an offset to sit level with the inputs. */
.cl-nested-head>.cl-icon-btn,
.cl-vg-main>.cl-icon-btn,
.cl-join-row>.cl-icon-btn {
    margin-top: 1.1rem;
}

.cl-icon-btn:hover {
    color: #e63757;
}

.cl-blank {
    text-align: center;
    padding: 3rem 1rem;
    color: #b1c2d9;
}

.cl-blank i {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
    display: block;
}

.cl-blank p {
    font-size: 0.85rem;
}

@media (max-width: 991.98px) {
    .cl-builder {
        grid-template-columns: 1fr;
    }

    .cl-rail {
        position: static;
    }

    .cl-rail-list {
        max-height: 12rem;
    }

    .cl-vg-main,
    .cl-join-row,
    .cl-nested-head {
        grid-template-columns: 1fr;
    }

    /* Stacked, the delete button would claim a full row of its own; pin it to
       the block's top-right corner instead. */
    .cl-nested-head,
    .cl-vg-main,
    .cl-join-row {
        position: relative;
        padding-right: 2rem;
    }

    .cl-nested-head>.cl-icon-btn,
    .cl-vg-main>.cl-icon-btn,
    .cl-join-row>.cl-icon-btn {
        position: absolute;
        top: 0;
        right: 0;
        margin-top: 0;
    }
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .cl-rail {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .cl-rail-item:hover {
        background: $dark-hover-bg !important;
    }

    .cl-rail-item.active {
        background: $dark-selected-bg !important;
        border-left-color: $dark-accent !important;
    }

    .cl-rail-name {
        color: $dark-text-primary !important;
    }

    .cl-rail-item.active .cl-rail-name {
        color: #7ab0ff !important;
    }

    .cl-rail-count,
    .cl-badge {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .cl-rail-title,
    .cl-rail-meta,
    .cl-rail-empty,
    .cl-label,
    .cl-card-meta,
    .cl-hint {
        color: $dark-text-muted !important;
    }

    .cl-detail-head,
    .cl-subsection-head,
    .cl-card-foot,
    .cl-card-body {
        border-color: $dark-border-color !important;
    }

    .cl-title-input {
        color: $dark-text-primary !important;
    }

    .cl-subtitle-input {
        color: $dark-text-secondary !important;
    }

    .cl-title-input:hover,
    .cl-subtitle-input:hover {
        border-color: $dark-border-color !important;
    }

    .cl-title-input:focus,
    .cl-subtitle-input:focus {
        background: $dark-input-bg !important;
        border-color: $dark-accent !important;
    }

    .cl-section-title,
    .cl-card-name {
        color: $dark-text-primary !important;
    }

    .cl-subsection-title {
        color: $dark-text-secondary !important;
    }

    .cl-empty {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
        color: $dark-text-muted !important;
    }

    .cl-card {
        background: $dark-hover-bg !important;
        border-color: $dark-border-color !important;
    }

    .cl-card-head:hover {
        background: $dark-hover-bg !important;
    }

    .cl-card-body {
        background: $dark-body-bg !important;
    }

    .cl-card-sql,
    .cl-inline-tag {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .cl-nested,
    .cl-join-row {
        background: $dark-elevated-bg !important;
        border-color: $dark-border-color !important;
        border-left-color: #444a52 !important;
    }

    .cl-nested:hover,
    .cl-join-row:hover,
    .cl-vg-row:hover {
        border-left-color: $dark-accent !important;
    }

    .cl-nested-body {
        border-top-color: $dark-border-color !important;
    }

    .cl-vg {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .cl-vg-row {
        background: $dark-card-bg !important;
        border-left-color: $dark-border-color !important;
    }

    .cl-vg-empty {
        color: $dark-text-muted !important;
    }

    .cl-blank {
        color: $dark-text-muted !important;
    }
}
</style>
